import { format, subYears } from "date-fns";
import { EnumPeopleContactMethod } from "gql/generated";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EVENT_DATE_FORMAT } from "shared/utils";
import { PersonContactInfoFormValues } from "../../usePersonContactInfoForm";

export const useCommonContactInfo = () => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_CONTACT_INFO_MODAL.COMMON_CONTACT_INFO",
  });

  const contactMethodTypes = useMemo(
    () =>
      Object.values(EnumPeopleContactMethod).map(contactMethod => ({
        label: t(`CONTACT_METHODS.${contactMethod.toUpperCase()}`),
        value: contactMethod,
      })),
    [t],
  );

  const { control, setValue } = useFormContext<PersonContactInfoFormValues>();
  const email = useWatch({ control, name: "email" });
  const contactMethod = useWatch({ control, name: "contactMethod" });
  const birthday = useWatch({ control, name: "birthday" });
  const optInEmail = useWatch({ control, name: "optInEmail" });
  const optInPhone = useWatch({ control, name: "optInPhone" });
  const optInSMS = useWatch({ control, name: "optInSMS" });
  const optInFax = useWatch({ control, name: "optInFax" });

  const optInOptions: {
    label: string;
    checked: boolean | undefined;
    name: "optInEmail" | "optInPhone" | "optInSMS" | "optInFax";
  }[] = useMemo(
    () => [
      {
        label: t("LABELS.OPT_IN.EMAIL"),
        checked: optInEmail,
        name: "optInEmail",
      },
      {
        label: t("LABELS.OPT_IN.PHONE"),
        checked: optInPhone,
        name: "optInPhone",
      },
      {
        label: t("LABELS.OPT_IN.SMS"),
        checked: optInSMS,
        name: "optInSMS",
      },
      {
        label: t("LABELS.OPT_IN.FAX"),
        checked: optInFax,
        name: "optInFax",
      },
    ],
    [optInEmail, optInPhone, optInSMS, optInFax, t],
  );

  const { minBirthday, maxBirthday } = useMemo(() => {
    const date = new Date();
    return {
      minBirthday: format(subYears(date, 120), EVENT_DATE_FORMAT),
      maxBirthday: format(date, EVENT_DATE_FORMAT),
    };
  }, []);

  return {
    contactMethodTypes,
    contactMethod,
    email,
    minBirthday,
    maxBirthday,
    setValue,
    birthday,
    optInOptions,
  };
};
